import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { Button, type ButtonProps, Collapse, message } from 'antd';
import { BooleanConditionEditorInput } from '../../../../../../inputs/expressions-editor/conditions/BooleanConditionEditorInput.tsx';
import { RichTextAdvancedInput } from '../../../../../../inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer/DrawerEditorButtonRow.tsx';
import { StyledDrawerCollapse } from '../../../../../../ui/drawer/StyledDrawerCollapse.tsx';
import { type IWidgetOption } from '../../../../Widget.ts';
import { createDefaultOptionValue } from './util/createDefaultOptionValue.ts';
import { widgetOptionValueValidator } from './util/widgetOptionValidator.ts';

const { Panel } = Collapse;

export interface IWidgetOptionValueEditDrawerProps {
  editor: RelationEditor<IWidgetOption>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  index: number | null;
  setIndex: (index: number | null) => void;
}

export function WidgetOptionValueEditDrawer(
  props: IWidgetOptionValueEditDrawerProps,
) {
  const { editor, buttonProps, index, setIndex } = props;

  return (
    <>
      <Button
        {...buttonProps}
        onClick={() => {
          setIndex(null);
          editor.handle.open(createDefaultOptionValue());
        }}
      >
        Opzione
      </Button>
      <Drawer
        maskClosable={false}
        title="Configurazione opzione"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={
          editor.handle.isVisible && editor.handle.current?.type === 'option'
        }
        destroyOnClose
      >
        <FormHandler
          layout="vertical"
          schema={widgetOptionValueValidator}
          initialValues={
            editor.handle.current ??
            widgetOptionValueValidator.cast({}, { assert: false })
          }
          onSubmit={async rawValues => {
            try {
              const values =
                await widgetOptionValueValidator.validate(rawValues);

              const type = editor.handle.apply(values, index);

              if (type === 'updated') {
                message.success('Opzione modificata');
              } else {
                message.success('Opzione aggiunta');
              }
            } catch (e) {
              // biome-ignore format: <tbd>
              message.error('Si è verificato un errore nella gestione dell\'opzione');
              console.error(e);
            }
          }}
        >
          <FormItem name="label" label="Etichetta">
            <RichTextAdvancedInput
              rows={4}
              name="label"
              menus={MenuPresets.InlineWithReturn}
            />
          </FormItem>
          <FormItem name="value" label="Valore">
            <TextInput name="value" />
          </FormItem>

          <StyledDrawerCollapse defaultActiveKey={['view']} ghost>
            <Panel header="Regole di Visualizzazione" key="view">
              <FormItem name="visible" label="Visibilità">
                <BooleanConditionEditorInput name="visible" label="Visibile" />
              </FormItem>
              <FormItem name="enabled" label="Abilitato">
                <BooleanConditionEditorInput name="enabled" label="Abilitato" />
              </FormItem>
            </Panel>
          </StyledDrawerCollapse>

          <DrawerEditorButtonRow editor={editor} />
        </FormHandler>
      </Drawer>
    </>
  );
}
